import { CloseOutlined } from '@mui/icons-material';
import {
  Alert,
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import TextInput from '../TextInput';

import ShiftsList from './ShiftsList';

import { GAEvent } from '@/constants/gaevents';
import { WORKERS_LISTING_LIMIT } from '@/constants/general';
import { ApiEndpoints, V3Endpoints } from '@/constants/urls';
import Table from '@/elements/Table';
import Text from '@/elements/Text';
import { useJobHireWorkerMutation, useJobOfferCreateMutation } from '@/graphql';
import useAuth from '@/hooks/useAuth';
import useDebouncedValue from '@/hooks/useDebouncedValue';
import useMediaQuery from '@/hooks/useMediaQuery';
import StaffingTabs from '@/routes/Agency/CreateGigOrder/JobEditor/Steps/StaffingStep/Common/StaffingTabs';
import {
  NotFoundAlert,
  getTabValue,
} from '@/routes/Agency/CreateGigOrder/JobEditor/Steps/StaffingStep/Common/StaffingUtils';
import StaffingWorkerProfileRow from '@/routes/Agency/CreateGigOrder/JobEditor/Steps/StaffingStep/Common/StaffingWorkerProfileRow';
import { JOB_HIRE_AND_OFFER_ERROR_MAP } from '@/routes/Agency/Order/InviteAndHireModal';
import { IconColor } from '@/styles/colors';
import { Job, JobTypeEnum, JobWorkerStatusEnum } from '@/types/graphql';
import useAnalytics from '@/util/analytics';
import axiosClient, { axiosRubyClient } from '@/util/axios/axiosClient';
import { handleMutationFormError } from '@/util/error';
import { getHiredWorkersByJobWorkers } from '@/util/job';
import { workerName } from '@/util/worker';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { FEATURE_TOGGLE } from '@/constants/featuretoggle';

type Props = {
  hideModal: (refetch?: boolean) => void;
  selectedJobs: Record<string, any>[];
  customerId: string;
  jobTaxType: string;
  showShiftList?: boolean;
  timezone?: string;
  qualityGateActive?: boolean;
};

const WorkerStaffingModel = ({
  selectedJobs,
  hideModal,
  customerId,
  jobTaxType,
  showShiftList = true,
  timezone,
  qualityGateActive = false,
}: Props) => {
  const phoneOnly = useMediaQuery('(max-width: 559px)');
  const phoneStyles = phoneOnly
    ? { height: '90vh', width: '100%', borderRadius: '20px' }
    : {};

  const [tab, setTab] = useState(0);
  const [workersList, setWorkersList] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const debSearch = useDebouncedValue(search, 400);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const { currentAdminIsCustomerAdmin: isClientAdmin } = useAuth();
  const containerRef = useRef<HTMLDivElement>(null);
  const { logEvent } = useAnalytics();
  const workerBadge = useFeatureValue(FEATURE_TOGGLE.WorkerBadge, false);

  const staffing = useRef(false);
  const [jobs, setJobs] = useState(selectedJobs);
  const [errors, setErrors] = useState<any[]>([]);
  const [isStaffing, setIsStaffing] = useState(false);
  const [createJobOffer] = useJobOfferCreateMutation({
    refetchQueries: ['GetOrder'],
  });
  const [hireWorker] = useJobHireWorkerMutation({
    refetchQueries: ['GetOrder'],
  });
  const jobInfo = jobs[0] as Job;
  const isLTA = jobInfo.jobType === JobTypeEnum.LTA;
  const selectedIds = jobs
    .filter((j) => j.hiredWorkersCount < j.quantity)
    .map((j) => j.id);

  // Check if at least one job requires background checks
  const backgroundCheckRequired = jobs.some(
    (job) => job.requireBackgroundChecks,
  );

  const jobFulfillment = useFeatureValue(FEATURE_TOGGLE.JobFulfillment, false);

  useEffect(() => {
    if (debSearch) {
      logEvent(GAEvent.WorkerListSearch);
    }
    fetchWorkers(1);
  }, [tab, debSearch]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      let isThrottled = false;

      const handleScroll = () => {
        // Prevent multiple rapid calls
        if (isThrottled) return;

        const scrollTop = container.scrollTop;
        const clientHeight = container.clientHeight;
        const scrollHeight = container.scrollHeight;

        // Add offset of 200px to trigger before hitting the bottom
        const offset = 200;
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - offset;

        if (isNearBottom && hasMore && !loading) {
          isThrottled = true;
          fetchWorkers(page + 1);
          setPage((prev) => prev + 1);

          // Reset throttle after a short delay
          setTimeout(() => {
            isThrottled = false;
          }, 300);
        }
      };

      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [hasMore, loading, debSearch, page]);

  const fetchWorkers = async (page) => {
    setLoading(true);
    try {
      const res = jobFulfillment
        ? await axiosRubyClient.get(ApiEndpoints.GET_WORKERS_LIST, {
            params: {
              skillId: Number(jobInfo.skill?.id),
              customerId: Number(customerId),
              addressId: Number(jobInfo.address.id),
              jobId: jobInfo.id,
              page,
              perPage: WORKERS_LISTING_LIMIT,
              search: debSearch,
              tab: getTabValue(tab, !!jobInfo.skill?.gravyTrained),
              workerType: isLTA ? 'lta' : 'gig',
              taxType: jobTaxType || 'all',
              backgroundCheckRequired: backgroundCheckRequired,
              badgeIds:
                workerBadge && jobInfo.certificates.length > 0
                  ? jobInfo.certificates.map((c) => c.id).join(',')
                  : null,
            },
          })
        : await axiosClient.post(V3Endpoints.GET_WORKERS_LIST, {
            skillId: Number(jobInfo.skill?.id),
            customerId: Number(customerId),
            addressId: Number(jobInfo.address.id),
            page,
            perPage: WORKERS_LISTING_LIMIT,
            search: debSearch,
            tab: getTabValue(tab, !!jobInfo.skill?.gravyTrained),
            workerType: isLTA ? 'lta' : 'gig',
            taxType: jobTaxType || 'all',
            backgroundCheckRequired: backgroundCheckRequired,
            badgeIds:
              workerBadge && jobInfo.certificates.length > 0
                ? jobInfo.certificates.map((c) => c.id).join(',')
                : null,
          });
      const respData = res?.data?.data || [];

      if (page > 1) {
        // For subsequent pages, filter out duplicates based on worker ID
        setWorkersList((prev) => {
          const existingIds = new Set(prev.map((worker) => worker.id));
          const newWorkers = respData.filter(
            (worker) => !existingIds.has(worker.id),
          );
          return [...prev, ...newWorkers];
        });
      } else {
        // For first page, set directly
        setWorkersList(respData);
      }

      setHasMore(respData.length > 0);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const staffingHandler = async (worker, mode, action = 'hire') => {
    if (mode === 'remove') return;
    const workerId = worker.id;
    setErrors([]);
    const checkProperty = action === 'invite' ? 'jobOffers' : 'jobWorkers';
    setIsStaffing(true);
    for (const job of jobs) {
      if (
        !selectedIds.includes(job.id) ||
        job[checkProperty].some(
          (w) =>
            w.worker.id === workerId.toString() &&
            w.status !== JobWorkerStatusEnum.DISMISSED &&
            w.status !== JobWorkerStatusEnum.DROPPED &&
            !w.rejectedAt,
        )
      )
        continue;
      try {
        if (action === 'invite') {
          await handleInvite(job, workerId);
        } else {
          await handleHire(job, workerId);
        }
      } catch (err) {
        handleMutationError(err, worker);
      }
    }
    setIsStaffing(false);
    staffing.current = true;
  };

  const handleHire = async (job, workerId) => {
    const resp = await hireWorker({
      variables: {
        workerId,
        jobId: job.id,
        payRate: null,
        forceHire: !isClientAdmin ? true : null,
      },
    });
    if (resp?.data?.jobHireWorker?.job) {
      logEvent(GAEvent.JobHireWorker, job.id, {
        hiredFrom: extractLocationFromURL(window.location.href),
        tab: getTabValue(tab, !!jobInfo.skill?.gravyTrained),
      });
      updateJobs(resp.data.jobHireWorker.job, workerId);
    }
  };

  const handleInvite = async (job, workerId) => {
    if (!isPastSchedule(job)) {
      const resp = await createJobOffer({
        variables: {
          workerId,
          jobId: job.id,
          forceInvite: !isClientAdmin ? true : null,
        },
      });
      if (resp?.data?.jobOfferCreate?.jobOffer) {
        logEvent(GAEvent.JobInviteWorker, job.id, {
          inviteSentFrom: extractLocationFromURL(window.location.href),
          tab: getTabValue(tab, !!jobInfo.skill?.gravyTrained),
        });
        updateJobs(job, workerId, resp.data.jobOfferCreate.jobOffer);
      }
    }
  };

  const extractLocationFromURL = (url) => {
    if (url.includes('/jobs/')) {
      return 'Job Details Page';
    } else if (url.includes('/orders/')) {
      return 'Order Details Page';
    }
    return 'Unknown';
  };

  const updateJobs = (updatedJob: any, workerId: any, jobOffer: any = null) => {
    setJobs((prevJobs) =>
      prevJobs.map((job) => {
        if (job.id === updatedJob.id) {
          return {
            ...job,
            hiredWorkersCount: updatedJob.hiredWorkersCount,
            jobWorkers: getHiredWorkersByJobWorkers(updatedJob.jobWorkers),
            jobOffers: jobOffer
              ? [...job.jobOffers, jobOffer]
              : job.jobOffers.filter(
                  (j) => j.worker.id !== workerId.toString(),
                ),
          };
        }
        return job;
      }),
    );
  };

  const handleMutationError = (err, worker) => {
    handleMutationFormError(err, {
      setFormError: (key, value) => {
        setErrors([
          {
            key,
            title: value?.title,
            msg: value?.message.replace('{0}', workerName(worker)),
          },
        ]);
      },
      errorMap: JOB_HIRE_AND_OFFER_ERROR_MAP,
    });
  };

  const checkWorkerHiredOrInvited = (workerId, action = '') => {
    const checkProperty = action === 'hire' ? 'jobWorkers' : 'jobOffers';
    return jobs.every((job) =>
      job[checkProperty].some(
        (w) =>
          w.worker.id === workerId.toString() &&
          w.status !== JobWorkerStatusEnum.DISMISSED &&
          w.status !== JobWorkerStatusEnum.DROPPED &&
          !w.rejectedAt,
      ),
    );
  };

  const [conflictWarning, setConflictWarning] = useState<{
    worker: Record<string, any>;
    mode?: string;
  } | null>(null);

  const handleInviteWithConflictCheck = (
    worker: Record<string, any>,
    mode?: string,
  ) => {
    if (worker.hasConflicts && mode !== 'remove') {
      setConflictWarning({ worker, mode });
    } else {
      staffingHandler(worker, mode, 'invite');
    }
  };

  const handleClose = () => hideModal(staffing.current);

  const isPastSchedule = (schedule) =>
    new Date(schedule.firstShiftStartAt).getTime() < new Date().getTime();

  const allSelectedSchedulesArePast = () => jobs.every(isPastSchedule);
  return (
    <Dialog
      PaperProps={{
        id: 'worker-staffing-model',
        style: {
          height: '92vh',
          width: '1100px',
          overflowX: 'hidden',
          borderRadius: '20px',
          maxHeight: 'calc(100% - 40px)',
          position: 'relative',
          ...phoneStyles,
        },
      }}
      fullScreen={phoneOnly}
      maxWidth={'lg'}
      open={true}
      sx={{
        '& .MuiDialog-container': phoneOnly
          ? { alignItems: 'end', marginBottom: '2px' }
          : {},
      }}
      onClose={handleClose}
    >
      {/* Loading Overlay */}
      {loading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            borderRadius: '20px',
          }}
        >
          <LinearProgress
            sx={{
              width: '200px',
              height: '4px',
            }}
          />
        </div>
      )}
      <DialogTitle style={{ padding: '10px 22px' }}>
        <Text
          size={'lg'}
          weight={'semibold'}
        >{`Staff Workers for ${jobInfo.skill.name}`}</Text>
        <IconButton
          aria-label="close"
          sx={{ float: 'right', padding: '2px', color: IconColor }}
          onClick={handleClose}
        >
          <CloseOutlined />
        </IconButton>
        {errors.length > 0 && (
          <Alert
            severity="warning"
            style={{ alignItems: 'center' }}
            onClose={() => setErrors([])}
          >
            <ul>
              {errors.map((e) => (
                <>
                  <li key={e.key} style={{ fontWeight: 'bold' }}>
                    {e.title}
                  </li>
                  <li key={e.key}>{e.msg}</li>
                </>
              ))}
            </ul>
          </Alert>
        )}
      </DialogTitle>
      <DialogContent
        ref={containerRef}
        id="worker-list"
        style={{ padding: '22px', paddingTop: '0px' }}
      >
        {showShiftList && (
          <ShiftsList
            isClientAdmin={isClientAdmin}
            isLTA={isLTA}
            jobs={jobs}
            phoneOnly={phoneOnly}
            selectedIds={selectedIds}
            timezone={timezone}
          />
        )}
        {isStaffing && <LinearProgress sx={{ height: '2px' }} />}
        <Divider sx={{ mt: '12px', mb: '8px' }} />
        {!isLTA && (
          <StaffingTabs
            addressId={jobInfo.address.id}
            custId={customerId}
            isLTA={isLTA}
            jobId={jobInfo.id}
            jobTaxType={jobTaxType}
            setTab={(tab) => {
              setSearch('');
              setTab(tab);
            }}
            skill={jobInfo.skill}
            tab={tab}
            badgeIds={
              workerBadge && jobInfo.certificates.length > 0
                ? jobInfo.certificates.map((c) => c.id).join(',')
                : null
            }
            backgroundCheckRequired={backgroundCheckRequired}
          />
        )}
        <div style={{ width: '100%', marginTop: '12px' }}>
          <TextInput
            id="search-GravyWorkers"
            placeholder="Search for GravyWorkers"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {qualityGateActive && (
          <div
            style={{
              backgroundColor: '#E7F4E680',
              border: '1px solid #A7DAA1',
              borderRadius: '4px',
              padding: '8px 16px',
              marginTop: '12px',
              fontFamily: 'Manrope',
              fontWeight: 600,
              fontSize: '12px',
              lineHeight: '140%',
              color: '#2E8722',
              width: '100%',
            }}
          >
            Dependability gate active — only highly dependable workers are
            eligible for hire.
          </div>
        )}
        <div style={{ width: '100%', marginTop: '18px' }}>
          <Table
            css={phoneOnly ? { tableLayout: 'fixed', overflowX: 'clip' } : {}}
          >
            <tbody>
              {workersList.map((w) => (
                <StaffingWorkerProfileRow
                  key={w.id}
                  buttonsDisableCheck={selectedIds.length === 0 || isStaffing}
                  hireButtonClick={staffingHandler}
                  inviteButtonClick={handleInviteWithConflictCheck}
                  inviteButtonDisable={
                    selectedIds.length === 0 ||
                    allSelectedSchedulesArePast() ||
                    isStaffing
                  }
                  isClientAdmin={isClientAdmin}
                  isHiredButton={checkWorkerHiredOrInvited(w.id, 'hire')}
                  isInvitedButton={checkWorkerHiredOrInvited(w.id)}
                  isLTA={isLTA || allSelectedSchedulesArePast()}
                  modalView={true}
                  skillName={jobInfo?.skill?.name}
                  worker={w}
                />
              ))}
            </tbody>
          </Table>
          {!loading && workersList.length === 0 && <NotFoundAlert tab={tab} />}
          {loading && workersList.length > 0 && <LinearProgress />}
        </div>
      </DialogContent>

      {/* Conflict warning dialog when inviting unavailable worker */}
      <Dialog open={!!conflictWarning} onClose={() => setConflictWarning(null)}>
        <DialogTitle>Worker Has Schedule Conflicts</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {conflictWarning && (
              <>
                {`${workerName(conflictWarning.worker)} has existing shift${
                  (conflictWarning.worker.conflictDates?.length || 0) > 1
                    ? 's'
                    : ''
                } on ${conflictWarning.worker.conflictDates?.join(', ') || 'conflicting dates'}.`}
                {conflictWarning.worker.firstConflictJob && (
                  <>
                    <br />
                    {`Conflicting shift on ${conflictWarning.worker.firstConflictJob.date || ''}${conflictWarning.worker.firstConflictJob.startTime ? ` (${conflictWarning.worker.firstConflictJob.startTime} - ${conflictWarning.worker.firstConflictJob.endTime})` : ''}`}
                  </>
                )}
                <br />
                They will receive your invite but are currently booked for{' '}
                {(conflictWarning.worker.conflictDates?.length || 0) > 1
                  ? 'these dates'
                  : 'this date'}
                .
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setConflictWarning(null)}>Cancel</MuiButton>
          <MuiButton
            variant="contained"
            onClick={() => {
              if (conflictWarning) {
                staffingHandler(
                  conflictWarning.worker,
                  conflictWarning.mode,
                  'invite',
                );
              }
              setConflictWarning(null);
            }}
          >
            Send Invite
          </MuiButton>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default WorkerStaffingModel;
